import { ReadonlyVec3 } from 'gl-matrix';
import { ChangeTracker } from '../changes/ChangeTracker';
import { RoiSnapshot, TrackableObject } from '../changes/DomainChange';
import { newRoiId, RoiId } from '../changes/ids';
import { DicomColor } from '../dicom/dicomColor';
import { DICOMApprovalStatus } from '../dicom/dicomConstants';
import { DicomRoi } from '../dicom/dicomRoi';
import {
    RoiGenerationAlgorithmType,
    RoiInterpretedType,
} from '../dicom/dicomstructureset';
import { ApprovedStructureError } from '../errors/DomainError';

export type RoiCategory =
    | 'Target'
    | 'OAR'
    | 'External'
    | 'Marker'
    | 'Support'
    | 'Other';

function deriveCategory(t: RoiInterpretedType | undefined): RoiCategory {
    switch (t) {
        case RoiInterpretedType.PTV:
        case RoiInterpretedType.CTV:
        case RoiInterpretedType.GTV:
            return 'Target';
        case RoiInterpretedType.ORGAN:
        case RoiInterpretedType.OAR:
        case RoiInterpretedType.AVOIDANCE:
            return 'OAR';
        case RoiInterpretedType.EXTERNAL:
            return 'External';
        case RoiInterpretedType.MARKER:
        case RoiInterpretedType.REGISTRATION:
        case RoiInterpretedType.ISOCENTER:
            return 'Marker';
        case RoiInterpretedType.BOLUS:
        case RoiInterpretedType.SUPPORT:
        case RoiInterpretedType.FIXATION:
            return 'Support';
        default:
            return 'Other';
    }
}

export class Roi implements TrackableObject {
    readonly trackableKind = 'roi' as const;
    readonly id: RoiId;
    private tracker: ChangeTracker | null = null;

    constructor(
        public readonly dicomRoi: DicomRoi,
        id: RoiId = newRoiId()
    ) {
        this.id = id;
    }

    get trackableId(): RoiId {
        return this.id;
    }

    attachTracker(tracker: ChangeTracker): void {
        this.tracker = tracker;
    }

    get roiNumber(): number {
        return this.dicomRoi.roiNumber;
    }

    get name(): string {
        return this.dicomRoi.name;
    }

    get color(): DicomColor {
        return this.dicomRoi.color;
    }

    get approvalStatus(): DICOMApprovalStatus {
        return this.dicomRoi.approvalStatus;
    }

    get isApproved(): boolean {
        return this.dicomRoi.isApproved;
    }

    get interpretedType(): RoiInterpretedType {
        return this.dicomRoi.interpretedType;
    }

    get category(): RoiCategory {
        return deriveCategory(this.interpretedType);
    }

    get contourCount(): number {
        return this.dicomRoi.contours.length;
    }

    get isEmpty(): boolean {
        return this.contourCount === 0;
    }

    get isPointRoi(): boolean {
        return this.dicomRoi.isPointRoi;
    }

    /**
     * Volume in cubic mm, or null when not yet computed.
     * TODO §2.2 — volume calculation lives in the engine and is out of scope
     * for this task. Wire in the engine-provided value when the volume
     * pipeline is connected.
     */
    get volumeMm3(): number | null {
        return null;
    }

    rename(newName: string): void {
        if (this.isApproved) {
            throw new ApprovedStructureError(
                `Cannot rename approved ROI ${this.dicomRoi.name}`
            );
        }
        this.dicomRoi.name = newName;
        this.tracker?.markChanged(this);
    }

    setColor(color: DicomColor): void {
        if (this.isApproved) {
            throw new ApprovedStructureError(
                `Cannot recolor approved ROI ${this.dicomRoi.name}`
            );
        }
        this.dicomRoi.color = color;
        this.tracker?.markChanged(this);
    }

    approve(_reviewer: string): void {
        this.dicomRoi.approvalStatus = DICOMApprovalStatus.APPROVED;
        this.tracker?.markChanged(this);
    }

    reject(_reviewer: string): void {
        this.dicomRoi.approvalStatus = DICOMApprovalStatus.REJECTED;
        this.tracker?.markChanged(this);
    }

    unapprove(): void {
        this.dicomRoi.approvalStatus = DICOMApprovalStatus.UNAPPROVED;
        this.tracker?.markChanged(this);
    }

    setInterpretedType(type: RoiInterpretedType): void {
        if (this.isApproved) {
            throw new ApprovedStructureError(
                `Cannot change interpreted type of approved ROI ${this.dicomRoi.name}`
            );
        }
        this.dicomRoi.interpretedType = type;
        this.tracker?.markChanged(this);
    }

    /**
     * Visibility is pure rendering state — editable even when the ROI
     * is approved (you can still show/hide an approved structure).
     */
    setVisibility(visible: boolean): void {
        this.dicomRoi.visible = visible;
        this.tracker?.markChanged(this);
    }

    /**
     * Contour line thickness is pure rendering state — editable even
     * when the ROI is approved.
     */
    setContourThickness(thickness: number): void {
        this.dicomRoi.contourThickness = thickness;
        this.tracker?.markChanged(this);
    }

    setDescription(description: string): void {
        if (this.isApproved) {
            throw new ApprovedStructureError(
                `Cannot change description of approved ROI ${this.dicomRoi.name}`
            );
        }
        this.dicomRoi.roiDescription = description;
        this.tracker?.markChanged(this);
    }

    setGenerationAlgorithm(algorithm: RoiGenerationAlgorithmType): void {
        if (this.isApproved) {
            throw new ApprovedStructureError(
                `Cannot change generation algorithm of approved ROI ${this.dicomRoi.name}`
            );
        }
        this.dicomRoi.roiGenerationAlgorithm = algorithm;
        this.tracker?.markChanged(this);
    }

    setElectronDensityOverride(value: number | undefined): void {
        if (this.isApproved) {
            throw new ApprovedStructureError(
                `Cannot change electron density override of approved ROI ${this.dicomRoi.name}`
            );
        }
        this.dicomRoi.electronDensityOverride = value;
        this.tracker?.markChanged(this);
    }

    setMassDensityOverride(value: number | undefined): void {
        if (this.isApproved) {
            throw new ApprovedStructureError(
                `Cannot change mass density override of approved ROI ${this.dicomRoi.name}`
            );
        }
        this.dicomRoi.massDensityOverride = value;
        this.tracker?.markChanged(this);
    }

    setPointCoordinates(coordinatesMm: ReadonlyVec3): void {
        if (this.isApproved) {
            throw new ApprovedStructureError(
                `Cannot change point coordinates of approved ROI ${this.dicomRoi.name}`
            );
        }
        this.dicomRoi.setPointCoordinates(coordinatesMm);
        this.tracker?.markChanged(this);
    }

    /** Engine signals contour pixels were edited; domain only records the change. */
    markContourEdited(): void {
        this.tracker?.markChanged(this);
    }

    /** Undo restored prior pixel state; emit a changeset entry so adapters resync. */
    markContourReverted(): void {
        this.tracker?.markChanged(this);
    }

    captureSnapshot(): RoiSnapshot {
        return {
            id: this.id,
            roiNumber: this.roiNumber,
            name: this.name,
            color: { ...this.color },
            approvalStatus: this.approvalStatus,
            interpretedType: this.interpretedType,
            contourCount: this.contourCount,
            volumeMm3: this.volumeMm3,
            isEmpty: this.isEmpty,
            isPointRoi: this.isPointRoi,
        };
    }
}
